import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	login,
	loginWithGoogle,
	isLoggedIn,
	requestPasswordReset,
	verifyResetCode,
	changePassword,
	getCurrentSubscription,
} from "../services/authService";
import { GOOGLE_CLIENT_ID } from "../services/apiConfig";

const STEPS = {
	LOGIN: "login",
	FORGOT_PASSWORD: "forgot_password",
	ENTER_CODE: "enter_code",
	NEW_PASSWORD: "new_password",
};

function Login() {
	const navigate = useNavigate();

	const navigateAfterLogin = async () => {
		const result = await getCurrentSubscription();
		if (result.success && result.subscription) {
			const proTierIds = [4, 6];
			if (proTierIds.includes(result.subscription.idAbonnementType)) {
				navigateAfterLogin();
				return;
			}
		}
		navigate("/dashboard");
	};

	const [step, setStep] = useState(STEPS.LOGIN);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [resetCode, setResetCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const googleButtonRef = useRef(null);

	const handleGoogleResponse = useCallback(async (response) => {
		setError("");
		setLoading(true);

		const result = await loginWithGoogle(response.credential);

		setLoading(false);

		if (result.success) {
			navigateAfterLogin();
		} else {
			setError(result.error);
		}
	}, [navigate]);

	useEffect(() => {
		// Redirect if already logged in
		if (isLoggedIn()) {
			navigateAfterLogin();
			return;
		}

		// Initialize Google Identity Services
		const initGoogleSignIn = () => {
			if (window.google?.accounts?.id) {
				window.google.accounts.id.initialize({
					client_id: GOOGLE_CLIENT_ID,
					callback: handleGoogleResponse,
				});

				if (googleButtonRef.current) {
					window.google.accounts.id.renderButton(googleButtonRef.current, {
						theme: "outline",
						size: "large",
						width: "100%",
						text: "signin_with",
						locale: "fr",
					});
				}
			}
		};

		// GIS script may not be loaded yet
		if (window.google?.accounts?.id) {
			initGoogleSignIn();
		} else {
			const interval = setInterval(() => {
				if (window.google?.accounts?.id) {
					clearInterval(interval);
					initGoogleSignIn();
				}
			}, 100);
			return () => clearInterval(interval);
		}
	}, [navigate, handleGoogleResponse]);

	const handleLogin = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		const result = await login(email, password);

		setLoading(false);

		if (result.success) {
			navigateAfterLogin();
		} else {
			setError(result.error);
		}
	};

	const handleForgotPassword = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		setLoading(true);

		const result = await requestPasswordReset(email);

		setLoading(false);

		if (result.success) {
			setSuccess("Un code a été envoyé à votre adresse email");
			setStep(STEPS.ENTER_CODE);
		} else {
			setError(result.error);
		}
	};

	const handleVerifyCode = async (e) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		const result = await verifyResetCode(email, resetCode);

		setLoading(false);

		if (result.success) {
			setStep(STEPS.NEW_PASSWORD);
		} else {
			setError(result.error);
		}
	};

	const handleChangePassword = async (e) => {
		e.preventDefault();
		setError("");

		if (newPassword.length < 6) {
			setError("Le mot de passe doit contenir au moins 6 caractères");
			return;
		}

		if (newPassword !== confirmPassword) {
			setError("Les mots de passe ne correspondent pas");
			return;
		}

		setLoading(true);

		const result = await changePassword(email, resetCode, newPassword);

		setLoading(false);

		if (result.success) {
			setSuccess("Mot de passe modifié avec succès");
			setStep(STEPS.LOGIN);
			setPassword("");
			setResetCode("");
			setNewPassword("");
			setConfirmPassword("");
		} else {
			setError(result.error);
		}
	};

	const goBackToLogin = () => {
		setStep(STEPS.LOGIN);
		setError("");
		setSuccess("");
		setResetCode("");
		setNewPassword("");
		setConfirmPassword("");
	};

	return (
		<div className="page">
			<div className="login-container">
				<div className="login-card">
					<h1 className="login-title">
						{step === STEPS.LOGIN && "Connexion"}
						{step === STEPS.FORGOT_PASSWORD && "Mot de passe oublié"}
						{step === STEPS.ENTER_CODE && "Vérification"}
						{step === STEPS.NEW_PASSWORD && "Nouveau mot de passe"}
					</h1>

					{error && <div className="login-error">{error}</div>}
					{success && <div className="login-success">{success}</div>}

					{step === STEPS.LOGIN && (
						<>
						<form onSubmit={handleLogin} className="login-form">
							<div className="form-group">
								<label htmlFor="email">Email</label>
								<input
									type="email"
									id="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="votre@email.com"
									required
									disabled={loading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="password">Mot de passe</label>
								<input
									type="password"
									id="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="Votre mot de passe"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Connexion..." : "Se connecter"}
							</button>

							<button
								type="button"
								className="forgot-password-link"
								onClick={() => setStep(STEPS.FORGOT_PASSWORD)}
							>
								Mot de passe oublié ?
							</button>
						</form>

						<div className="google-signin-separator">
							<span className="separator-line"></span>
							<span className="separator-text">ou</span>
							<span className="separator-line"></span>
						</div>

						<div className="google-signin-container" ref={googleButtonRef}></div>
						</>
					)}

					{step === STEPS.FORGOT_PASSWORD && (
						<form onSubmit={handleForgotPassword} className="login-form">
							<p className="login-description">
								Entrez votre email pour recevoir un code de réinitialisation
							</p>

							<div className="form-group">
								<label htmlFor="reset-email">Email</label>
								<input
									type="email"
									id="reset-email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="votre@email.com"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Envoi..." : "Envoyer le code"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Retour à la connexion
							</button>
						</form>
					)}

					{step === STEPS.ENTER_CODE && (
						<form onSubmit={handleVerifyCode} className="login-form">
							<p className="login-description">
								Entrez le code à 6 chiffres reçu par email
							</p>

							<div className="form-group">
								<label htmlFor="code">Code de vérification</label>
								<input
									type="text"
									id="code"
									value={resetCode}
									onChange={(e) => setResetCode(e.target.value)}
									placeholder="000000"
									maxLength={6}
									className="code-input"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Vérification..." : "Vérifier le code"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Annuler
							</button>
						</form>
					)}

					{step === STEPS.NEW_PASSWORD && (
						<form onSubmit={handleChangePassword} className="login-form">
							<p className="login-description">
								Choisissez votre nouveau mot de passe
							</p>

							<div className="form-group">
								<label htmlFor="new-password">Nouveau mot de passe</label>
								<input
									type="password"
									id="new-password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									placeholder="Minimum 6 caractères"
									required
									disabled={loading}
								/>
							</div>

							<div className="form-group">
								<label htmlFor="confirm-password">Confirmer le mot de passe</label>
								<input
									type="password"
									id="confirm-password"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Confirmez votre mot de passe"
									required
									disabled={loading}
								/>
							</div>

							<button
								type="submit"
								className="login-button"
								disabled={loading}
							>
								{loading ? "Modification..." : "Changer le mot de passe"}
							</button>

							<button
								type="button"
								className="back-link"
								onClick={goBackToLogin}
							>
								Annuler
							</button>
						</form>
					)}
				</div>
			</div>
		</div>
	);
}

export default Login;
